import type { CSSProperties } from 'react'
import { useContainerWidth } from '@/features/journey/hooks/use-container-width'
import { useActivePlan } from '@/features/journey/hooks/use-journey'
import { practiceStackLayout } from '@/features/journey/lib/practice-stack-layout'
import {
  buildWeekPath,
  DASH,
  DAY_PATH,
  MILESTONE_TOP_Y,
  NODE_CIRCLE,
  nodeCenter,
  STROKE_WIDTH,
} from '@/features/journey/lib/serpentine'
import { connectorPath } from '@/features/journey/lib/step-connector'
import {
  gridHeight,
  nodeCenters,
  STEP_MILESTONE_SPACING,
  STEP_NODE,
  STEP_PAIR_SPACING,
  STEP_ROW_PITCH,
  stepRows,
} from '@/features/journey/lib/step-map-metrics'
import { usePlanFraming } from '@/features/plan/hooks/use-plan-framing'
import { cn } from '@/shared/lib/utils'

// Grey block with the same diagonal light sweep as the plan skeleton.
function Shimmer({
  className,
  style,
}: {
  className?: string
  style?: CSSProperties
}) {
  return (
    <div
      className={cn('relative overflow-hidden bg-app-grey-200', className)}
      style={style}
    >
      <div
        className="pointer-events-none absolute inset-0 -skew-x-12"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
          animation: 'skeleton-shimmer 1.8s ease-in-out infinite',
        }}
      />
    </div>
  )
}

// One week of the serpentine map: dashed path + node placeholders + milestone.
function WeekSkeleton() {
  const { ref, width } = useContainerWidth<HTMLDivElement>()

  return (
    <div>
      <div
        ref={ref}
        className="relative w-full"
        style={{ height: MILESTONE_TOP_Y }}
      >
        {width > 0 && (
          <>
            <svg
              aria-hidden
              className="absolute inset-0"
              width={width}
              height={MILESTONE_TOP_Y}
              viewBox={`0 0 ${width} ${MILESTONE_TOP_Y}`}
              fill="none"
            >
              <path
                d={buildWeekPath(width)}
                stroke="var(--color-app-grey-200)"
                strokeWidth={STROKE_WIDTH}
                strokeDasharray={DASH.join(' ')}
                strokeLinecap="round"
              />
            </svg>
            {DAY_PATH.map((placement, i) => {
              const center = nodeCenter(placement, width)
              return (
                <Shimmer
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional nodes
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: NODE_CIRCLE,
                    height: NODE_CIRCLE,
                    left: center.x - NODE_CIRCLE / 2,
                    top: center.y - NODE_CIRCLE / 2,
                  }}
                />
              )
            })}
          </>
        )}
      </div>
      <div className="mx-[16px]">
        <Shimmer className="h-[74px] rounded-[24px]" />
      </div>
    </div>
  )
}

const SKELETON_ROWS = stepRows([1, 2, 3, 4, 5, 6, 7])
const MILESTONE_CARD = 70
const SHEET = practiceStackLayout(1).front

// One section of the step map: divider, stacks on the connector, milestone card.
function StepSectionSkeleton() {
  const { ref, width } = useContainerWidth<HTMLDivElement>()
  const grid = gridHeight(SKELETON_ROWS.length)
  const height = grid + STEP_MILESTONE_SPACING + MILESTONE_CARD
  const path =
    width > 0
      ? connectorPath(
          {
            nodes: nodeCenters(SKELETON_ROWS, width),
            width,
            height,
            completedNodeCount: 0,
          },
          'dashed',
        )
      : null
  return (
    <div className="flex flex-col gap-[20px] px-[16px] pt-[6px] pb-[20px]">
      <div className="flex items-center gap-[12px] py-[14px]">
        <div className="h-px flex-1 bg-app-grey-100" />
        <Shimmer className="h-[18px] w-[140px] rounded-full" />
        <div className="h-px flex-1 bg-app-grey-100" />
      </div>
      <div className="px-[24px]">
        <div ref={ref} className="relative" style={{ height }}>
          {path && (
            <svg
              aria-hidden
              className="absolute inset-0 overflow-visible"
              width={width}
              height={height}
              fill="none"
            >
              <path
                d={path.d}
                stroke="var(--color-app-grey-200)"
                strokeWidth={3}
                strokeLinecap="round"
                strokeDasharray="12 12"
              />
            </svg>
          )}
          <div
            className="relative flex flex-col"
            style={{ gap: STEP_ROW_PITCH - STEP_NODE }}
          >
            {SKELETON_ROWS.map((row) => (
              <div
                key={row.days[0]}
                className="flex justify-center"
                style={{ gap: STEP_PAIR_SPACING }}
              >
                {row.days.map((day) => (
                  <div
                    key={day}
                    className="relative"
                    style={{ width: STEP_NODE, height: STEP_NODE }}
                  >
                    <Shimmer
                      className="-translate-x-1/2 absolute left-1/2 rounded-[24px]"
                      style={{
                        width: SHEET.width,
                        height: SHEET.height,
                        top: SHEET.top,
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div
            className="absolute inset-x-0 bottom-0 flex items-center gap-[11.5px] rounded-[24px] bg-white p-[8px] shadow-[inset_0_0_0_2px_var(--color-app-grey-200)]"
            style={{ height: MILESTONE_CARD }}
          >
            <Shimmer className="size-[54px] shrink-0 rounded-[18px]" />
            <Shimmer className="h-[16px] w-[160px] rounded-full" />
          </div>
        </div>
      </div>
    </div>
  )
}

function StepJourneySkeleton() {
  // The title lands with the plan, before the map — drawn for real it holds its exact lines.
  const title = useActivePlan().data?.planTitle
  return (
    <div className="h-full overflow-hidden">
      <div className="mx-auto w-full max-w-md pt-[16px]">
        {title ? (
          <h1 className="px-[24px] pb-[24px] text-center font-display font-medium text-[32px] text-app-grey-900 leading-[40px] lining-nums">
            {title}
          </h1>
        ) : (
          <div className="flex justify-center px-[24px] pb-[24px]">
            <Shimmer className="h-[40px] w-[260px] rounded-[12px]" />
          </div>
        )}
        <div className="px-[24px] pb-[16px]">
          <div className="flex flex-col gap-[6px] rounded-[24px] bg-app-grey-100 p-[6px]">
            <div className="flex h-[20px] items-center justify-center pt-[2px]">
              <Shimmer className="h-[16px] w-[180px] rounded-full" />
            </div>
            <div className="flex min-h-[74px] items-center gap-[12px] rounded-[20px] bg-white px-[16px] py-[11px]">
              <div className="flex flex-1 flex-col gap-[6px]">
                <Shimmer className="h-[14px] w-[72px] rounded-full" />
                <Shimmer className="h-[18px] w-[150px] rounded-full" />
              </div>
              <Shimmer className="size-[50px] rounded-full" />
            </div>
          </div>
        </div>
        <StepSectionSkeleton />
      </div>
    </div>
  )
}

export function JourneySkeleton() {
  const isStep = usePlanFraming() === 'step'
  if (isStep) return <StepJourneySkeleton />
  return (
    <div className="h-full overflow-hidden">
      <div className="mx-auto w-full max-w-md pt-[8px]">
        {/* Goal card */}
        <div className="px-[16px] pb-[8px]">
          <Shimmer className="h-[96px] rounded-[32px]" />
        </div>
        {/* Week label */}
        <div className="flex items-center gap-[12px] px-[16px] py-[24px]">
          <div className="h-px flex-1 bg-app-grey-100" />
          <Shimmer className="h-[18px] w-[120px] rounded-full" />
          <div className="h-px flex-1 bg-app-grey-100" />
        </div>
        <WeekSkeleton />
        <WeekSkeleton />
      </div>
    </div>
  )
}
